import React, { Component } from "react";
import PropTypes from "prop-types";
import _ from "lodash";

import WrapDialogue from "../common/WrapDialogue";
import NormalTextField from "../common/NormalTextField";
import NormalBigButton from "../common/NormalBigButton";
import NormalSmallButton from "../common/NormalSmallButton";
import ActionButton from "../common/ActionButton";
import Config from "../../config";
import { createRequestOptions } from "../../services/utils";
import { Mixpanel } from "../../mixpanel";
import events from "../../services/events";
import S3Upload from "react-s3-uploader/s3upload.js";
import UUID from "uuid/v4";
import Delete from "material-ui/svg-icons/action/delete-forever";
import { IconButton } from "@material-ui/core";

const style = {
  container: {
    display: "flex",
    flexFlow: "column",
  },
  buttons: {
    display: "flex",
    flexFlow: "row",
  },
};

class PushNotificationSender extends Component {
  constructor(props) {
    super(props);
    this.state = {
      subject: props.subject,
      text: props.text,
      allowReply: true,
      // media keys set after upload
      photoKey: null,
      videoKey: null,
      // selection (no upload yet)
      selectedPhotoFile: null,
      selectedPhotoPreviewUrl: null,
      selectedVideoFile: null,
      selectedVideoPreviewUrl: null,
      // upload state during send
      isUploadingPhoto: false,
      isUploadingVideo: false,
      progressPhoto: 0,
      progressVideo: 0,
      photoError: "",
      videoError: "",
      isSending: false,
    };

    this.send = this.send.bind(this);
    this.sendToUser = this.sendToUser.bind(this);
    this.sendToApp = this.sendToApp.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (this.state.subject == "") {
      this.setState({
        subject: nextProps.subject,
        text: nextProps.text,
      });
    }
  }

  handleChange = (name) => (event) => {
    this.setState({ [name]: event.target.checked });
  };

  handlePhotoSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Allowed extensions based on ImagePicker.js
    const allowedExtensions = [
      ".xbm", ".tif", ".pjp", ".apng", ".svgz", ".jpg", ".jpeg", ".heif", ".ico", 
      ".tiff", ".webp", ".jfif", ".heic", ".gif", ".svg", ".png", ".bmp", ".pjpeg", ".avif"
    ];
    
    const fileName = file.name.toLowerCase();
    const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (!isValidExtension) {
      alert(`Unsupported image type. Allowed: ${allowedExtensions.join(", ")}`);
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      alert("Image is too big. Please choose an image under 10MB.");
      return;
    }
    const preview = URL.createObjectURL(file);
    this.setState({
      selectedPhotoFile: file,
      selectedPhotoPreviewUrl: preview,
      photoKey: null,
      progressPhoto: 0,
      photoError: "",
    });
  };

  handleVideoSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Allowed extensions for videos based on VideoPicker.js
    const allowedExtensions = [
      ".ogm", ".wmv", ".mpg", ".webm", ".ogv", ".mov", ".asx", ".mpeg", ".mp4", ".m4v", ".avi"
    ];
    const fileName = file.name.toLowerCase();
    const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (!isValidExtension) {
      alert(`Unsupported video type. Allowed: ${allowedExtensions.join(", ")}`);
      return;
    }

    if (file.size > 150 * 1024 * 1024) {
      alert("Video is too big. Please choose a video under 150MB.");
      return;
    }
    const preview = URL.createObjectURL(file);
    this.setState({
      selectedVideoFile: file,
      selectedVideoPreviewUrl: preview,
      videoKey: null,
      progressVideo: 0,
      videoError: "",
    });
  };

  uploadPhoto = () => {
    return new Promise((resolve, reject) => {
      const file = this.state.selectedPhotoFile;
      if (!file) return resolve(null);
      this.setState({ isUploadingPhoto: true, progressPhoto: 0, photoError: "" });
      const ext = file.type === "image/png" ? ".png" : file.type === "image/webp" ? ".webp" : ".jpg";
      const objectKey = `images/${this.props.userId}/${UUID()}${ext}`;
      const getSignedUrl = (f, callback) => {
        const options = createRequestOptions({
          userId: this.props.userId,
          bucketName: Config.s3.bucket,
          objectKey,
          contentType: f.type,
        });
        fetch(Config.serverUrl + "/GetS3SignedUrl", options)
          .then((r) => r.json())
          .then((json) => {
            const ret = _.get(json, "response");
            const signedUrl = _.replace(ret.signedUrl, "http://", "https://");
            callback({ signedUrl, fileName: objectKey, publicUrl: "/s3/uploads/" + objectKey, fileKey: objectKey });
          })
          .catch(() => {
            this.setState({ isUploadingPhoto: false, photoError: "Error getting signed URL" });
            reject("signed-url-error");
          });
      };
      new S3Upload({
        files: [file],
        getSignedUrl,
        onProgress: (percent) => this.setState({ progressPhoto: percent }),
        onError: (message) => {
          this.setState({ isUploadingPhoto: false, photoError: "Error uploading the image: " + message });
          reject(message);
        },
        onFinishS3Put: (data) => {
          const fullUrl = Config.s3.s3Url + "/" + data.fileName;
          this.setState({ isUploadingPhoto: false, photoKey: fullUrl, progressPhoto: 100 });
          resolve(fullUrl);
        },
      });
    });
  };

  uploadVideo = () => {
    return new Promise((resolve, reject) => {
      const file = this.state.selectedVideoFile;
      if (!file) return resolve(null);
      this.setState({ isUploadingVideo: true, progressVideo: 0, videoError: "" });
      const ext = file.type === "video/quicktime" ? ".mov" : ".mp4";
      const objectKey = `videos/${this.props.userId}/${this.props.appId}/${UUID()}${ext}`;
      const getSignedUrl = (f, callback) => {
        const options = createRequestOptions({
          userId: this.props.userId,
          bucketName: Config.s3.bucket,
          objectKey,
          contentType: f.type,
        });
        fetch(Config.serverUrl + "/GetS3SignedUrl", options)
          .then((r) => r.json())
          .then((json) => {
            const ret = _.get(json, "response");
            const signedUrl = _.replace(ret.signedUrl, "http://", "https://");
            callback({ signedUrl, fileName: objectKey, publicUrl: "/s3/uploads/" + objectKey, fileKey: objectKey });
          })
          .catch(() => {
            this.setState({ isUploadingVideo: false, videoError: "Error getting signed URL" });
            reject("signed-url-error");
          });
      };
      new S3Upload({
        files: [file],
        getSignedUrl,
        onProgress: (percent) => this.setState({ progressVideo: percent }),
        onError: (message) => {
          this.setState({ isUploadingVideo: false, videoError: "Error uploading the video: " + message });
          reject(message);
        },
        onFinishS3Put: (data) => {
          const fullUrl = Config.s3.s3Url + "/" + data.fileName;
          this.setState({ isUploadingVideo: false, videoKey: fullUrl, progressVideo: 100 });
          resolve(fullUrl);
        },
      });
    });
  };

  clearPhoto = () => {
    if (this.state.selectedPhotoPreviewUrl) {
      URL.revokeObjectURL(this.state.selectedPhotoPreviewUrl);
    }
    this.setState({ selectedPhotoFile: null, selectedPhotoPreviewUrl: null, photoKey: null, progressPhoto: 0, photoError: "" });
  };

  clearVideo = () => {
    if (this.state.selectedVideoPreviewUrl) {
      URL.revokeObjectURL(this.state.selectedVideoPreviewUrl);
    }
    this.setState({ selectedVideoFile: null, selectedVideoPreviewUrl: null, videoKey: null, progressVideo: 0, videoError: "" });
  };

  async send() {
    try {
      this.setState({ isSending: true });
      const [photoKey, videoKey] = await Promise.all([this.uploadPhoto(), this.uploadVideo()]);

      if (photoKey) this.setState({ photoKey });
      if (videoKey) this.setState({ videoKey });
      switch (this.props.type) {
        case "user":
          await this.sendToUser();
          break;
        case "app":
          await this.sendToApp();
          break;
        case "selected":
          await this.sendToSelect();
          break;
        default:
          await this.sendToUser();
      }
      this.setState({ isSending: false });
      this.refs.showSentSuccessful.open();
    } catch (e) {
      this.setState({ isSending: false });
      alert("Upload failed. Please try again.");
    }
  }

  sendToUser(recipientUserId) {
    Mixpanel.track("Patient send message", {
      "Patient ID": this.props.userId,
      "Recipient User ID": recipientUserId
        ? recipientUserId
        : this.props.recipientUserId,
    });

    const options = createRequestOptions({
      userId: this.props.userId,
      appId: this.props.appId,
      appCode: this.props.appCode,
      recipientUserId: recipientUserId
        ? recipientUserId
        : this.props.recipientUserId,
      sender: this.props.sender,
      allowReply: this.state.allowReply,
      subject: this.state.subject,
      text: this.state.text,
      imageUrl: this.state.photoKey,
      videoUrl: this.state.videoKey,
    });
    return fetch(Config.commServerUrl + "/SendNotificationToUser", options)
      .then((response) => {
        Mixpanel.track("Send push notification", {
          "Recipient User ID": recipientUserId
            ? recipientUserId
            : this.props.recipientUserId,
          Platform: "Web",
        });

        events().trackUserEvent(
          "Send Message",
          this.props.customApp ? this.props.customApp.appId : null,
          null,
          null,
          null,
          this.props.userId,
          recipientUserId ? recipientUserId : this.props.recipientUserId,
          "portal",
          this.props.customApp ? this.props.customApp.appInfo.appName : null,
          null,
          this.props.customApp
            ? null
            : JSON.stringify({
                directMessage: true,
                isReply: this.props.isReply,
              })
        );
      })
      .catch((error) => {
        console.error("Error sending notification to user: " + error);
      });
  }

  sendToSelect() {
    this.props.selectedList.map((u) => {
      this.sendToUser(u.userId);
    });
  }

  sendToApp() {
    const options = createRequestOptions({
      userId: this.props.userId,
      appId: this.props.appId,
      appCode: this.props.whiteLabelSettings.appCode,
      sender: this.props.sender,
      allowReply: this.state.allowReply,
      subject: this.state.subject,
      text: this.state.text,
      imageUrl: this.state.photoKey,
      videoUrl: this.state.videoKey,
    });
    return fetch(Config.commServerUrl + "/SendNotificationToApp", options)
      .then((response) => {
        //console.log("Send notification to app response: ", response.json());
      })
      .catch((error) => {
        //console.error("Error sending notification to app: " + error);
      });
  }

  render() {
    return (
      <div>
        <div style={style.container}>
          <NormalTextField
            floatingLabelText="Subject"
            fullWidth={true}
            errorText={
              _.isEmpty(this.state.subject) ? "This field is required" : ""
            }
            value={this.state.subject}
            onChange={(event) => {
              this.setState({ subject: event.target.value });
            }}
            whiteLabelSettings={this.props.whiteLabelSettings}
          />
          <NormalTextField
            floatingLabelText="Text"
            fullWidth={true}
            multiLine={true}
            rows={10}
            rowsMax={20}
            value={this.state.text}
            onChange={(event) => {
              this.setState({ text: event.target.value });
            }}
            whiteLabelSettings={this.props.whiteLabelSettings}
          />
          <div style={{ marginTop: 20, display: 'flex', gap: 5 }}>
            {/* Image Upload */}
            <div style={{ marginBottom: 15 }}>
              <NormalSmallButton
                containerElement="label"
                label={this.state.selectedPhotoFile ? "Replace Photo" : "Attach Photo"}
                disabled={this.state.isUploadingPhoto || this.state.isSending}
              >
                <input
                  type="file"
                  style={{ display: "none" }}
                  accept="image/*"
                  onChange={this.handlePhotoSelect}
                />
              </NormalSmallButton>
              <span style={{ fontSize: 12, color: "grey", marginLeft: 10 }}>
                Max 10MB
              </span>
              {this.state.selectedPhotoPreviewUrl && (
                <div style={{ marginTop: 12, position: "relative", width: 120, height: 120 }}>
                  <img
                    src={this.state.selectedPhotoPreviewUrl}
                    alt="Attached"
                    style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 3, border: "1px solid #ccc" }}
                  />
                  <IconButton
                    style={{
                      position: "absolute",
                      top: -10,
                      right: -10,
                      backgroundColor: "#fafafa",
                      width: 30,
                      height: 30,
                      padding: 0,
                      border: "1px solid #ccc",
                      zIndex: 10,
                    }}
                    onClick={this.clearPhoto}
                  >
                    <Delete style={{ width: 20, height: 20 }} />
                  </IconButton>
                </div>
              )}
              {this.state.isUploadingPhoto && (
                <div style={{ fontSize: 12, color: "grey", marginTop: 5 }}>
                  Uploading image... {this.state.progressPhoto}%
                </div>
              )}
            </div>

            {/* Video Upload */}
            <div style={{ marginBottom: 15 }}>
              <NormalSmallButton
                containerElement="label"
                label={this.state.selectedVideoFile ? "Replace Video" : "Attach Video"}
                disabled={this.state.isUploadingVideo || this.state.isSending}
              >
                <input
                  type="file"
                  style={{ display: "none" }}
                  accept="video/*"
                  onChange={this.handleVideoSelect}
                />
              </NormalSmallButton>
              <span style={{ fontSize: 12, color: "grey", marginLeft: 10 }}>
                Max 150MB
              </span>
              {this.state.selectedVideoPreviewUrl && (
                <div style={{ marginTop: 12, position: "relative", width: 200, height: 120 }}>
                  <video
                    src={this.state.selectedVideoPreviewUrl}
                    style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 3, border: "1px solid #ccc" }}
                  />
                  <IconButton
                    style={{
                      position: "absolute",
                      top: -10,
                      right: -10,
                      backgroundColor: "#fafafa",
                      width: 30,
                      height: 30,
                      padding: 0,
                      border: "1px solid #ccc",
                      zIndex: 10,
                    }}
                    onClick={this.clearVideo}
                  >
                    <Delete style={{ width: 20, height: 20 }} />
                  </IconButton>
                </div>
              )}
              {this.state.isUploadingVideo && (
                <div style={{ fontSize: 12, color: "grey", marginTop: 5 }}>
                  Uploading video... {this.state.progressVideo}%
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={style.buttons}>
          <NormalBigButton
            label="Cancel"
            onClick={() => {
              this.clearPhoto();
              this.clearVideo();
              this.props.onComplete();
            }}
          />
          <ActionButton
            label={this.state.isSending ? "Sending..." : "Send"}
            disabled={this.state.isSending || this.state.isUploadingPhoto || this.state.isUploadingVideo}
            onClick={() => {
              this.send();
            }}
            whiteLabelSettings={this.props.whiteLabelSettings}
          />
        </div>
        <WrapDialogue
          ref="showSentSuccessful"
          title={"Message Sent Successfully"}
          content={
            <div style={{ textAlign: "center" }}>
              <h3>Your Message will be received shortly.</h3>
              <ActionButton
                style={{ width: 300, margin: 10 }}
                label="Ok"
                onClick={() => {
                  this.refs.showSentSuccessful.close();
                  this.props.onComplete();
                }}
              />
            </div>
          }
        />
      </div>
    );
  }
}

PushNotificationSender.propTypes = {
  type: PropTypes.string.isRequired,
  userId: PropTypes.number.isRequired,
  appId: PropTypes.number,
  appCode: PropTypes.string,
  recipientUserId: PropTypes.number,
  subject: PropTypes.string,
  text: PropTypes.string,
  onComplete: PropTypes.func,
};

export default PushNotificationSender;



// WEBPACK FOOTER //
// ./src/components/common/PushNotificationSender.js